import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { PageHeader } from "../../components/Layout";
import { toast } from "sonner";
import { Buildings, FloppyDisk, EnvelopeSimple, Phone, MapPin, Globe, Certificate, Tag, Trash, PencilSimple, Plus, Check, X } from "@phosphor-icons/react";

const emptyForm = {
  name: "",
  logo_url: "",
  website: "",
  contact_email: "",
  contact_phone: "",
  address: "",
  city: "",
  country: "",
  tagline: "",
  description: "",
  accreditation: "",
  certificate_bg_file_id: "",
  certificate_logo_file_id: "",
};

export default function InstitutionProfile() {
  const [inst, setInst] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/institutions/me");
      setInst(r.data);
      setForm({
        name: r.data.name || "",
        logo_url: r.data.logo_url || "",
        website: r.data.website || "",
        contact_email: r.data.contact_email || "",
        contact_phone: r.data.contact_phone || "",
        address: r.data.address || "",
        city: r.data.city || "",
        country: r.data.country || "",
        tagline: r.data.tagline || "",
        description: r.data.description || "",
        accreditation: r.data.accreditation || "",
        certificate_bg_file_id: r.data.certificate_bg_file_id || "",
        certificate_logo_file_id: r.data.certificate_logo_file_id || "",
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load institution");
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => { load(); }, []);

  const completeness = React.useMemo(() => {
    const fields = ["name", "logo_url", "website", "contact_email", "contact_phone", "address", "city", "country", "tagline", "description", "accreditation"];
    const filled = fields.filter((f) => (form[f] || "").toString().trim().length > 0).length;
    return Math.round((filled / fields.length) * 100);
  }, [form]);

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Institution name is required"); return; }
    setSaving(true);
    try {
      // Empty string == "cleared" — falsy check in extensions.py falls back to template/institution defaults.
      const r = await api.put("/institutions/me", form);
      setInst(r.data);
      toast.success("Institution profile updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <div className="text-sm text-zinc-500">Loading…</div>;
  if (!inst) return <div className="text-sm text-zinc-500">No institution associated with this account.</div>;

  return (
    <div>
      <PageHeader
        overline="Institution"
        title="Institution profile"
        subtitle="Manage your organization's branding, contact information, and public details visible to students."
      />

      {/* Completeness */}
      <div className="border border-zinc-200 p-5 mb-6" data-testid="inst-profile-completeness">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Profile completeness</div>
          <div className="font-mono-tab text-sm font-bold">{completeness}%</div>
        </div>
        <div className="h-2 bg-zinc-100">
          <div className={`h-2 ${completeness >= 80 ? "bg-green-600" : completeness >= 50 ? "bg-amber-500" : "bg-zinc-900"}`} style={{ width: `${completeness}%` }} />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Left branding */}
        <div className="border border-zinc-200 p-6">
          <div className="flex flex-col items-center text-center">
            {form.logo_url ? (
              <img
                src={form.logo_url}
                alt="Logo"
                className="w-28 h-28 object-contain border border-zinc-200 p-2 bg-white"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            ) : (
              <div className="w-28 h-28 flex items-center justify-center bg-zinc-100 text-zinc-400">
                <Buildings size={64} weight="light" />
              </div>
            )}
            <div className="mt-4 font-display text-lg font-bold">{form.name || inst.name}</div>
            <div className="text-xs text-zinc-500 mt-1 font-mono">{inst.code}</div>
            {form.tagline && <div className="text-xs text-zinc-600 mt-2 italic">&ldquo;{form.tagline}&rdquo;</div>}
            <div className="mt-3 text-[10px] uppercase tracking-[0.2em] border border-zinc-300 px-2 py-1">
              Institution ID: {inst.institution_id}
            </div>
          </div>

          <div className="mt-6 space-y-2 text-xs">
            <div className="flex items-center justify-between p-2 bg-amber-50 border border-amber-200">
              <span className="text-amber-800 inline-flex items-center gap-1 font-semibold uppercase tracking-wider text-[10px]">
                Wallet
              </span>
              <span className="font-mono font-bold text-base text-amber-900" data-testid="inst-profile-balance">
                {(inst.credits || 0).toLocaleString()} coins
              </span>
            </div>
            {inst.valid_from && (
              <div className="flex items-center justify-between">
                <span className="text-zinc-500">Valid from</span>
                <span className="font-mono">{inst.valid_from}</span>
              </div>
            )}
            {inst.valid_until && (
              <div className="flex items-center justify-between">
                <span className="text-zinc-500">Valid until</span>
                <span className="font-mono">{inst.valid_until}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-zinc-500">Status</span>
              <span className={`inline-block px-2 py-0.5 border text-[10px] uppercase tracking-wider ${inst.is_active !== false ? "border-green-300 text-green-700 bg-green-50" : "border-zinc-300 text-zinc-600 bg-zinc-50"}`}>
                {inst.is_active !== false ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
        </div>

        {/* Right form */}
        <div className="md:col-span-2 border border-zinc-200 p-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4 font-semibold">Organization details</div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Institution name" required>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
                data-testid="inst-profile-name-input"
              />
            </Field>
            <Field label="Website" icon={<Globe size={11} />}>
              <input
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                placeholder="https://…"
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
                data-testid="inst-profile-website-input"
              />
            </Field>
            <Field label="Contact email" icon={<EnvelopeSimple size={11} />}>
              <input
                type="email"
                value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
                data-testid="inst-profile-email-input"
              />
            </Field>
            <Field label="Contact phone" icon={<Phone size={11} />}>
              <input
                value={form.contact_phone}
                onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
                data-testid="inst-profile-phone-input"
              />
            </Field>
            <Field label="City" icon={<MapPin size={11} />}>
              <input
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
                data-testid="inst-profile-city-input"
              />
            </Field>
            <Field label="Country">
              <input
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
                data-testid="inst-profile-country-input"
              />
            </Field>
          </div>

          <Field label="Logo URL" className="mt-4">
            <input
              value={form.logo_url}
              onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
              placeholder="https://cdn.example.com/logo.png"
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="inst-profile-logo-input"
            />
          </Field>

          <Field label="Address" className="mt-4">
            <input
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="inst-profile-address-input"
            />
          </Field>

          <Field label="Tagline" className="mt-4">
            <input
              value={form.tagline}
              onChange={(e) => setForm({ ...form, tagline: e.target.value })}
              placeholder="A short institutional motto"
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="inst-profile-tagline-input"
            />
          </Field>

          <Field label="Description" className="mt-4">
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              placeholder="What is your institution known for?"
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="inst-profile-description-input"
            />
          </Field>

          <Field label="Accreditation" icon={<Certificate size={11} />} className="mt-4">
            <input
              value={form.accreditation}
              onChange={(e) => setForm({ ...form, accreditation: e.target.value })}
              placeholder="e.g. NAAC A+, ABET, etc."
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
              data-testid="inst-profile-accreditation-input"
            />
          </Field>

          <div className="mt-6 flex justify-end">
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-zinc-800 disabled:opacity-50"
              data-testid="inst-profile-save-btn"
            >
              <FloppyDisk size={14} /> {saving ? "Saving…" : "Save institution"}
            </button>
          </div>
        </div>
      </div>

      {/* Certificate Design panel */}
      <div className="mt-8 border border-zinc-200 p-6" data-testid="inst-cert-design">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">Certificate design</div>
            <div className="font-display text-lg font-bold mt-1">Custom institution certificate</div>
            <div className="text-xs text-zinc-500 mt-1">Upload your own landscape A4 background and optional logo. Overrides the built-in templates for every certificate issued under this institution.</div>
          </div>
          <PreviewButton bgFileId={form.certificate_bg_file_id} logoFileId={form.certificate_logo_file_id} instId={inst.institution_id} />
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-4">
          <CertAssetUploader
            label="Background artwork"
            hint="Full pre-designed landscape A4 (3508×2480 @ 300dpi recommended). PNG/JPG, max 5 MB. Only the student name, exam title, score, signature block and QR are drawn on top."
            fileId={form.certificate_bg_file_id}
            onChange={(id) => setForm({ ...form, certificate_bg_file_id: id })}
            testId="cert-bg"
            aspect="aspect-[297/210]"
          />
          <CertAssetUploader
            label="Institution logo/seal (optional)"
            hint="Small transparent PNG rendered at the top-center of the certificate above the student name. Aspect ratio preserved."
            fileId={form.certificate_logo_file_id}
            onChange={(id) => setForm({ ...form, certificate_logo_file_id: id })}
            testId="cert-logo"
            aspect="aspect-square"
          />
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-zinc-800 disabled:opacity-50"
            data-testid="inst-cert-design-save-btn"
          >
            <FloppyDisk size={14} /> {saving ? "Saving…" : "Save design"}
          </button>
        </div>
      </div>

      <CategoriesManager />
      <SmsSettings />
    </div>
  );
}

function SmsSettings() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testMobile, setTestMobile] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/sms/status");
      setStatus(r.data);
    } catch {
      setStatus(null);
    } finally { setLoading(false); }
  };
  useEffect(() => { loadStatus(); }, []);

  const runTest = async () => {
    if (!/^[6-9]\d{9}$/.test(testMobile)) {
      toast.error("Enter a valid 10-digit Indian mobile number");
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post("/admin/sms/test", { mobile: testMobile });
      setTestResult(r.data);
      if (r.data.ok) toast.success("Test SMS accepted by MSG91 — check your phone");
      else toast.error(r.data.error || "Test failed");
    } catch (e) {
      setTestResult({ ok: false, error: e.response?.data?.detail || "Request failed" });
      toast.error(e.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  const StatusDot = ({ ok, label }) => (
    <div className="flex items-center gap-2 text-xs">
      <span className={`inline-block w-2 h-2 rounded-full ${ok ? "bg-emerald-500" : "bg-red-500"}`} />
      <span className={ok ? "text-zinc-900" : "text-zinc-500"}>{label}</span>
    </div>
  );

  return (
    <div className="border border-zinc-200 p-6 sm:p-8 mt-6" data-testid="sms-settings">
      <div className="mb-5">
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold mb-1 inline-flex items-center gap-2">
          <EnvelopeSimple size={12} weight="fill" /> Mobile OTP / SMS
        </div>
        <h2 className="font-display text-xl font-bold">MSG91 configuration</h2>
        <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
          These credentials control whether login SMS actually reaches your users. Set them in <span className="font-mono bg-zinc-100 px-1">backend/.env</span> on your server and restart the API — then use the test box below to confirm delivery.
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : !status ? (
        <div className="text-sm text-zinc-500">Only visible to institution admins and super admins.</div>
      ) : (
        <>
          <div className={`border p-4 mb-5 ${status.ready ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`} data-testid="sms-status-banner">
            <div className="text-sm font-semibold text-zinc-900 mb-2">
              {status.ready ? "SMS delivery is ready" : "SMS not fully configured"}
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
              <StatusDot ok={status.authkey_set} label={`AUTHKEY ${status.authkey_set ? `(${status.authkey_masked})` : "missing"}`} />
              <StatusDot ok={status.template_id_set} label={`TEMPLATE_ID ${status.template_id_set ? "set" : "missing (recommended for DLT)"}`} />
              <StatusDot ok={!!status.sender_id} label={`Sender ID: ${status.sender_id || "default"}`} />
              <StatusDot ok={status.sms_retriever_hash_set} label={`Android retriever hash ${status.sms_retriever_hash_set ? "set" : "missing (auto-fill off)"}`} />
            </div>
            {!status.template_id_set && (
              <div className="mt-3 text-[11px] text-amber-800">
                Without a DLT-approved <span className="font-mono">MSG91_TEMPLATE_ID</span>, MSG91 may still accept the request but Indian telcos can filter the message out. Create a template in the MSG91 dashboard and set the ID.
              </div>
            )}
            {status.dev_mode && (
              <div className="mt-2 text-[11px] text-zinc-600">
                <span className="font-mono">ALLOW_DEV_OTP=1</span> is set — the OTP is echoed back to the client when a send fails. Unset this in production.
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">Test send to a real number</div>
              <div className="flex items-stretch border border-zinc-300 focus-within:border-zinc-900">
                <span className="inline-flex items-center px-3 text-sm font-mono text-zinc-600 bg-zinc-50 border-r border-zinc-300">+91</span>
                <input
                  type="tel"
                  inputMode="numeric"
                  placeholder="98765 43210"
                  value={testMobile}
                  onChange={(e) => setTestMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  className="flex-1 px-3 py-2 text-sm focus:outline-none font-mono tracking-wider"
                  data-testid="sms-test-input"
                />
              </div>
            </div>
            <button
              onClick={runTest}
              disabled={testing || testMobile.length !== 10}
              className="bg-zinc-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-zinc-800 disabled:opacity-50"
              data-testid="sms-test-btn"
            >
              {testing ? "Sending…" : "Send test OTP"}
            </button>
          </div>

          {testResult && (
            <div className={`mt-4 border p-3 text-xs ${testResult.ok ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-red-300 bg-red-50 text-red-800"}`} data-testid="sms-test-result">
              <div className="font-semibold mb-1">
                {testResult.ok ? "✓ Accepted by MSG91" : "✗ Failed"} <span className="text-zinc-500 font-mono text-[10px]">(status {testResult.status ?? "n/a"})</span>
              </div>
              {testResult.error && <div className="mb-1">Error: {testResult.error}</div>}
              {testResult.dev_code && <div>Dev OTP (echoed because dev mode is on): <span className="font-mono font-bold">{testResult.dev_code}</span></div>}
              {testResult.raw && (
                <details className="mt-1"><summary className="cursor-pointer text-[10px] uppercase tracking-wider">Raw MSG91 response</summary>
                  <pre className="mt-1 text-[10px] font-mono bg-white/60 p-2 overflow-auto">{JSON.stringify(testResult.raw, null, 2)}</pre>
                </details>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CategoriesManager() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingLabel, setEditingLabel] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/exam-categories");
      setCats(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load categories");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const doCreate = async () => {
    const label = newLabel.trim();
    if (!label) { toast.error("Enter a category name"); return; }
    setCreating(true);
    try {
      await api.post("/exam-categories", { label });
      setNewLabel("");
      toast.success(`Category "${label}" added`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Add failed");
    } finally { setCreating(false); }
  };

  const startEdit = (c) => {
    setEditingId(c.category_id);
    setEditingLabel(c.label);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditingLabel("");
  };
  const doRename = async (c) => {
    const label = editingLabel.trim();
    if (!label) { toast.error("Label required"); return; }
    if (label === c.label) { cancelEdit(); return; }
    setBusyId(c.category_id);
    try {
      await api.put(`/exam-categories/${c.category_id}`, { label });
      toast.success("Category renamed");
      cancelEdit();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rename failed");
    } finally { setBusyId(null); }
  };

  const doDelete = async (c) => {
    if (!window.confirm(`Delete category "${c.label}"? This cannot be undone.`)) return;
    setBusyId(c.category_id);
    try {
      await api.delete(`/exam-categories/${c.category_id}`);
      toast.success("Category deleted");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setBusyId(null); }
  };

  return (
    <div className="border border-zinc-200 p-6 sm:p-8 mt-6" data-testid="categories-manager">
      <div className="mb-5">
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold mb-1 inline-flex items-center gap-2">
          <Tag size={12} weight="fill" /> Exam categories
        </div>
        <h2 className="font-display text-xl font-bold">Sectors &amp; courses</h2>
        <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
          Categories are used to filter and badge exams in the catalog. The 11 built-in sectors can be renamed to fit your institute&rsquo;s vocabulary but cannot be deleted. Any sectors you add can be freely renamed or removed (as long as no exam still uses them).
        </p>
      </div>

      {/* Add new */}
      <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-6">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">New category</div>
          <input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") doCreate(); }}
            placeholder="e.g. Marketing, Pharma, Civil Engineering"
            className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
            data-testid="cat-new-input"
          />
        </div>
        <button
          onClick={doCreate}
          disabled={creating || !newLabel.trim()}
          className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-sm font-medium hover:bg-zinc-800 disabled:opacity-50"
          data-testid="cat-add-btn"
        >
          <Plus size={14} /> {creating ? "Adding…" : "Add"}
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : cats.length === 0 ? (
        <div className="text-sm text-zinc-500 border border-dashed border-zinc-300 p-6 text-center">No categories yet.</div>
      ) : (
        <div className="border border-zinc-200" data-testid="cat-list">
          {cats.map((c, i) => {
            const isEd = editingId === c.category_id;
            return (
              <div
                key={c.category_id}
                className={`p-3 sm:p-4 flex items-center gap-3 ${i < cats.length - 1 ? "border-b border-zinc-200" : ""}`}
                data-testid={`cat-row-${c.slug}`}
              >
                <Tag size={14} className="text-zinc-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  {isEd ? (
                    <input
                      autoFocus
                      value={editingLabel}
                      onChange={(e) => setEditingLabel(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") doRename(c);
                        if (e.key === "Escape") cancelEdit();
                      }}
                      className="w-full border border-zinc-900 px-2 py-1 text-sm focus:outline-none"
                      data-testid={`cat-edit-input-${c.slug}`}
                    />
                  ) : (
                    <div className="text-sm font-medium truncate">{c.label}</div>
                  )}
                  <div className="text-[10px] uppercase tracking-wider text-zinc-400 mt-0.5 flex items-center gap-2">
                    <span className="font-mono">{c.slug}</span>
                    {c.is_system && (
                      <span className="border border-zinc-200 bg-zinc-50 text-zinc-500 px-1.5 py-0.5">System</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {isEd ? (
                    <>
                      <button
                        onClick={() => doRename(c)}
                        disabled={busyId === c.category_id}
                        className="p-2 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                        data-testid={`cat-save-${c.slug}`}
                        title="Save"
                      >
                        <Check size={13} />
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="p-2 border border-zinc-300 hover:bg-zinc-50"
                        data-testid={`cat-cancel-${c.slug}`}
                        title="Cancel"
                      >
                        <X size={13} />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => startEdit(c)}
                        className="p-2 border border-zinc-300 hover:bg-zinc-50"
                        data-testid={`cat-edit-${c.slug}`}
                        title="Rename"
                      >
                        <PencilSimple size={13} />
                      </button>
                      <button
                        onClick={() => doDelete(c)}
                        disabled={c.is_system || busyId === c.category_id}
                        className="p-2 border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid={`cat-delete-${c.slug}`}
                        title={c.is_system ? "System categories cannot be deleted" : "Delete"}
                      >
                        <Trash size={13} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PreviewButton({ bgFileId, logoFileId, instId }) {
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      const qs = new URLSearchParams({ template: bgFileId ? "custom" : "classic", institution_id: instId });
      if (bgFileId) qs.set("bg_file_id", bgFileId);
      if (logoFileId) qs.set("logo_file_id", logoFileId);
      const r = await api.get(`/certificates/preview?${qs.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setBusy(false); }
  };
  return (
    <button
      onClick={open}
      disabled={busy}
      className="inline-flex items-center gap-2 border border-zinc-300 px-3 py-2 text-xs hover:border-zinc-900 disabled:opacity-50"
      data-testid="inst-cert-preview-btn"
    >
      <Certificate size={12} /> {busy ? "Loading…" : "Preview certificate"}
    </button>
  );
}

function CertAssetUploader({ label, hint, fileId, onChange, testId, aspect }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef(null);
  const url = fileId ? `${(process.env.REACT_APP_BACKEND_URL || "")}/api/files/${fileId}?auth=${encodeURIComponent(localStorage.getItem("session_token") || "")}` : "";

  const doUpload = async (file) => {
    if (!file.type.startsWith("image/")) { toast.error("PNG/JPG only"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("File too large (max 5 MB)"); return; }
    // Validate landscape orientation before uploading (client-side hint; backend still stores anything).
    const img = new Image();
    img.src = URL.createObjectURL(file);
    await new Promise((res) => { img.onload = res; img.onerror = res; });
    URL.revokeObjectURL(img.src);
    if (img.width && img.height && img.width < img.height && testId === "cert-bg") {
      if (!window.confirm("This image looks portrait-oriented. Backgrounds work best in landscape A4. Continue anyway?")) return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(res.data.file_id);
      toast.success(`${label} uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <div className="border border-zinc-200 p-4" data-testid={`${testId}-uploader`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 font-semibold">{label}</div>
      <div className={`w-full ${aspect} bg-zinc-50 border border-dashed border-zinc-300 flex items-center justify-center overflow-hidden`}>
        {fileId ? (
          <img
            src={url}
            alt="Certificate asset"
            className="w-full h-full object-contain"
            data-testid={`${testId}-preview-img`}
            onError={(e) => { e.target.style.opacity = 0.3; }}
          />
        ) : (
          <div className="text-xs text-zinc-400">No file uploaded</div>
        )}
      </div>
      <div className="text-[11px] text-zinc-500 mt-2 leading-relaxed">{hint}</div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) doUpload(f); }}
        data-testid={`${testId}-input`}
      />
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="border border-zinc-300 px-3 py-1.5 text-xs hover:border-zinc-900 disabled:opacity-50"
          data-testid={`${testId}-upload-btn`}
        >
          {uploading ? "Uploading…" : fileId ? "Replace" : "Upload image"}
        </button>
        {fileId && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="border border-zinc-300 px-3 py-1.5 text-xs text-red-700 hover:border-red-500"
            data-testid={`${testId}-remove-btn`}
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, required, icon, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 inline-flex items-center gap-1">
        {icon} {label} {required && <span className="text-red-600">*</span>}
      </div>
      {children}
    </label>
  );
}
